import { ChangeDetectionStrategy, Component, computed, HostListener, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { PricingStore } from "./reference-set-pricing/s25.event.pricing.store";
import { S25EventPricingComponent } from "./reference-set-pricing/s25.event.pricing.component";
import { S25PricingModule } from "./s25.pricing.module";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { EventStateConst } from "../../services/event/event.state.const";
import { S25Number } from "../../util/core/s25.number";

/**
 * Which pricing page an event opens. Reference sets are the shape only the newer page can read, so
 * an event the server still bills the older way is handed to the page that predates them. The
 * invoices decide it, which means waiting for them either way.
 */
@TypeManagerDecorator("s25-ng-pricing-tab")
@Component({
    selector: "s25-ng-pricing-tab",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25EventPricingComponent, S25PricingModule, S25LoadingInlineStaticComponent, S25IconComponent],
    template: `
        @if (!decided()) {
            <s25-ng-loading-inline-static />
            @if (pricingBuilding()) {
                <p class="pricing-status" role="status">Building pricing for this event...</p>
            }
        } @else if (noPricing()) {
            <div class="no-pricing">
                @if (reloadingForEventState()) {
                    <s25-ng-loading-inline-static [text]="''" />
                    <p>Loading pricing for this event…</p>
                } @else {
                    <s25-ng-icon [type]="'pricing'" [size]="48" [ariaLabel]="false" />
                    <h2>No Pricing</h2>
                    <p>This event has no pricing. Events in the Draft state are not priced.</p>
                }
            </div>
        } @else if (usesReferenceSets()) {
            <s25-ng-event-pricing />
        } @else {
            <s25-ng-pricing [itemId]="eventId()!" />
        }
    `,
    styles: `
        s25-ng-loading-inline-static {
            display: block;
            width: fit-content;
            margin: auto;
        }

        .pricing-status {
            padding: 1rem 0;
            text-align: center;
        }

        .no-pricing {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem 1rem;
            text-align: center;
            color: var(--color--text-muted);
        }

        .no-pricing h2 {
            margin: 1rem 0 0.5rem 0;
            font-size: 1.25rem;
            color: var(--color--text);
        }

        .no-pricing p {
            margin: 0;
            font-size: 1rem;
        }

        .no-pricing ::ng-deep svg {
            opacity: 0.65;
        }
    `,
})
export class S25PricingTabComponent {
    private readonly pricingStore = inject(PricingStore);

    readonly eventId = this.pricingStore.eventId;
    readonly pricingBuilding = this.pricingStore.pricingBuilding;
    readonly usesReferenceSets = this.pricingStore.usesReferenceSets;
    readonly noPricing = this.pricingStore.noPricing;
    readonly reloadingForEventState = this.pricingStore.reloadingForEventState;

    /**
     * Whether the invoices can answer the question. A load that failed says nothing about how the
     * event is billed, and is taken the same way as invoices carrying no reference set: the older
     * page, which reports a failure of its own if the same request fails for it. The event itself
     * is waited for too, since the Draft branch ahead of the fork cannot be read without it.
     */
    readonly decided = computed(
        () =>
            !!this.eventId() &&
            this.pricingStore.eventLoaded() &&
            (this.pricingStore.invoicesLoaded() || !!this.pricingStore.invoiceError()),
    );

    @HostListener(`window:${EventStateConst.updateState}`, ["$event"])
    onEventStateUpdate(event: CustomEvent) {
        if (S25Number.parseInt(event.detail?.eventId) !== this.eventId()) return;
        this.pricingStore.noteEventStateChanged();
    }
}
